import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import type { DropdownOption } from 'ui-components/components/dropdown/dropdown-types';
import { GuidService } from 'ui-components/services/guid-service';

import { usePackingSlipFormat } from '../../../../../../hooks/use-seller-setting';
import { useVisiblePackingSlips } from '../../../../../../hooks/use-visible-packing-slips';
import { PackingSlipFormat } from '../../../../printing/document-option-modal/packing-slip/constants';
import type { WorkflowActionInputProps } from '../../../registries/registry-types';
import { messages } from '../messages';

const getOptionLabel = (option: DropdownOption) => option.name as string;
const getOptionValue = (option: DropdownOption) => String(option.packingSlipId);

export const PackingSlipDropdown: React.FC<WorkflowActionInputProps> = ({ className, draft, onUpdate }) => {
  const intl = useIntl();
  const id = useMemo(() => GuidService.newGuid(), []);
  const packingSlips = useVisiblePackingSlips();
  const packingSlipFormat = usePackingSlipFormat();

  const options = useMemo(() => {
    const sizeFilter = packingSlipFormat === PackingSlipFormat.HalfLetter ? PackingSlipFormat.Letter : packingSlipFormat;
    return packingSlips.filter((ps: { size: string }) => ps.size === sizeFilter);
  }, [packingSlips, packingSlipFormat]);

  const handleSelect = useCallback(
    (value: string) => {
      onUpdate({ intParameter: value ? Number(value) : null });
    },
    [onUpdate],
  );

  return (
    <SearchableDropdownField
      className={className}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      id={id}
      onItemSelected={handleSelect}
      options={options}
      placeholder={intl.formatMessage(messages.packingSlipDropdownPlaceholder)}
      selectedItemValue={draft.intParameter !== null ? String(draft.intParameter) : undefined}
    />
  );
};
